<template>
  <div>
    <div class="text-container md:md-text-container">
      <div v-if="!consentComplete" class="grid grid-cols-1 md:grid-cols-10 gap-10">
        <div class="md:col-span-8">
          <Heading text="Help develop our research"/>
        </div>
        <div class="md:col-span-8">
          We would like your consent to use the data you’ve provided in our research. We will not
          save your email address or have any way to trace the data back to you.
        </div>
        <div class="md:col-span-5 grid grid-cols-1 gap-10 data-container md:md-data-container">
          <div class="checkbox-container md:md-checkbox-container">
            <input
              type="checkbox"
              v-model="dataUsageConsent"
              class="checkbox md:md-checkbox"
            /> I agree to take part in this research
          </div>
          <div>
            For how this data is used and stored by >
            <a class="privacy-policy-link" v-on:click="showPrivacyPolicy='vis'">
              Visualogical
            </a>
          </div>
          <div>
            If you would like us to delete this data at any time, please email
            studio@visualogical.co.uk and quote `{{$store.state.sessionId}}`. This code will also
            be in the email you send yourself with your Psyche-Selfie.
          </div>
          <Button v-bind:onClick="processConsent" :noMargin="true" text="Continue"/>
        </div>
        <div class="md:col-span-5">
          <img class="meme-image" :src="$store.state.finalMeme"/>
        </div>
      </div>
      <div v-else class="grid grid-cols-1 md:grid-cols-10 gap-10">
        <div class="md:col-span-8">
          <Heading text="Save your Psyche-Selfie"/>
        </div>
        <div class="md:col-span-8">
          To email yourself a copy of your Psyche-Selfie, please enter your email address:
        </div>
        <div class="md:col-span-5 grid grid-cols-3 gap-10">
          <div class="col-span-3 grid grid-cols-12 gap-3">
            <div v-if="!emailSent" class="col-span-8 md:col-span-12 lg:col-span-9">
              <input
                class="email-input md:md-email-input"
                v-model="emailAddress"
                autocomplete="off"
                v-on:keyup.enter="sendEmail"
                v-bind:disabled="emailSending"
              />
            </div>
            <div v-if="!emailSent" class="col-span-2">
              <Button
                v-bind:onClick="sendEmail"
                v-bind:disabled="emailSending"
                :noMargin="true"
                text="Send"
              />
            </div>
            <div v-else class="col-span-3 email-sent md:md-email-sent">
              Artwork Sent
            </div>
          </div>
          <div class="col-span-3">
            <div>
            Or download it to your device
            </div>
            <div>
            <button class="button md:md-button" v-on:click="downloadClick">
              <a
                :href="$store.state.finalMeme"
                download="psyche-selfie.jpg"
              >
                Download
              </a>
            </button>
            </div>
          </div>
          <div class="col-span-3">
            Thank you for taking part. Now view other Psyche-Selfies and find out more about your
            own below.
          </div>
          <div class="col-span-3">
            <button class="button md:md-button">
              <a
                href="https://gallery.visualogical.co.uk/happyplace/"
                target="_blank"
              >
                View gallery
              </a>
            </button>
          </div>
          <div class="col-span-3">
            <button class="button md:md-button">
              <a
                href="https://www.museumofthehome.org.uk/explore/things-to-do/happy-place/understand-your-psyche-selfie/"
                target="_blank"
              >
                About your Psyche-Selfie
              </a>
            </button>
          </div>
        </div>
        <div class="md:col-span-5">
          <img class="meme-image" :src="$store.state.finalMeme"/>
        </div>
      </div>
    </div>
    <div v-if="showPrivacyPolicy" class="privacy-policy-container">
      <div class="privacy-policy">
        <div
          class="privacy-policy-content md:md-privacy-policy-content"
        >
          <PrivacyPolicy
            v-if="showPrivacyPolicy==='sf'"
            :content="sayFromagePrivacyPolicy"
          />
          <PrivacyPolicy
            v-if="showPrivacyPolicy==='vis'"
            :content="visualogicalPrivacyPolicy"
          />
        </div>
        <button
          class="button md:md-button"
          v-on:click="showPrivacyPolicy=null">
          Back
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import marked from 'marked';
import sfpp from '@/assets/privacy_policies/say_fromage_privacy_policy.md';
import vpp from '@/assets/privacy_policies/visualogical_privacy_policy.md';
import Button from '@/components/Button.vue';
import Heading from '@/components/Heading.vue';
import PrivacyPolicy from '@/components/PrivacyPolicy.vue';

export default {
  name: 'Sharing',
  components: {
    Button,
    Heading,
    PrivacyPolicy,
  },
  data() {
    return {
      consentComplete: false,
      dataUsageConsent: false,
      emailAddress: null,
      emailSent: false,
      emailSending: false,
      fileDownloaded: false,
      showGoBackPrompt: false,
      sayFromagePrivacyPolicy: '',
      visualogicalPrivacyPolicy: '',
      showPrivacyPolicy: null,
    };
  },
  methods: {
    downloadClick() {
      this.fileDownloaded = true;
    },
    sendEmail() {
      if (!this.emailAddressIsValid()) {
        return;
      }

      this.emailSending = true;
      axios
        .post(
          `${process.env.VUE_APP_BACKEND_URL}/api/sessions/send-email/`,
          {
            uuid: this.$store.state.sessionId,
            address: this.emailAddress,
          },
        )
        .then((response) => {
          this.emailSending = false;
          if (response.status === 200) {
            this.emailSent = true;
          }
        })
        .catch((error) => {
          this.$store.commit('setErrorMessage', error);
        });
    },
    emailAddressIsValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.emailAddress);
    },
    flattenData() {
      const url = `${process.env.VUE_APP_BACKEND_URL}/api/sessions/flatten-data/?session=${this.$store.state.sessionId}`;
      axios
        .get(url)
        .catch((error) => {
          this.$store.commit('setErrorMessage', error);
        });
    },
    processConsent() {
      if (!this.dataUsageConsent) {
        const url = `${process.env.VUE_APP_BACKEND_URL}/api/sessions/delete-session/?session=${this.$store.state.sessionId}`;
        axios
          .get(url)
          .then(() => {
            this.flattenData();
          })
          .catch((error) => {
            this.$store.commit('setErrorMessage', error);
          });
      } else {
        this.flattenData();
      }
      this.consentComplete = true;
    },
    loadPrivacyPolicies() {
      this.sayFromagePrivacyPolicy = marked(sfpp);
      this.visualogicalPrivacyPolicy = marked(vpp);
    },
  },
  mounted() {
    this.loadPrivacyPolicies();
  },
};
</script>

<style scoped>
input[type='checkbox']{
  width: 1em !important;
  height: 1em !important;
  margin: 0 10px 0 20px;
  -webkit-appearance: none;
  -moz-appearance: none;
  -o-appearance: none;
  appearance: none;
  box-shadow: none;
  font-size: 2em;
  text-align: center;
  line-height: 1em;
  background: #fff;
}

input[type='checkbox']:checked:after {
  content: '\e900';
  color: #5e5b5c;
  font-family: 'Icomoon';
}

input[type='checkbox']:focus{
    outline: none;
}

.button {
  background-color: #5e5b5c;
  border-radius: 5rem;
  color: #fff;
  font-size: 1em;
  padding: .25em 1.75em;
}

.button:disabled {
  color: #bbb;
}

.checkbox {
  margin: 0 10px 0 20px;
}

.checkbox-container {
  align-items: center;
  display: flex;
  background-color: #eeedee;
  padding: .5em;
  width: 100%;
}

.data-container {
  font-size: .75em;
}

.email-sent {
  font-size: .75rem;
  padding: .25em 0;
}

.email-input {
  background-color: #ecebeb;
  border-radius: 2rem;
  font-size: .75rem;
  outline: none;
  padding: .25em 1.75em;
  text-transform: lowercase;
  width: 100%;
}

.email-input:disabled {
  color: #999;
}

.meme-image {
  border-width: 2px;
  filter: drop-shadow(-8px 8px 8px grey);
}

.privacy-links {
  list-style-type: disc;
  margin-left: 1em;
}

.privacy-policy {
  margin: auto;
}

.privacy-policy-container {
  background-color: #00000066;
  display: flex;
  height: 100vh;
  position: absolute;
  top: 0;
  width: 100vw;
  z-index: 9999;
}

.privacy-policy-content {
  background-color: white;
  height: 85vh;
  margin: auto;
  margin-bottom: 2vh;
  overflow: scroll;
  padding: 1em;
  text-align: left;
  width: 85vw;
}

.privacy-policy-link {
  color: #5e5b5c;
  text-decoration: underline;
}

.title {
  color: #9a9899;
  font-weight: bold;
}

@responsive {
  .md-button {
    font-size: 1em;
  }

  .md-checkbox {
    height: 40px;
    width: 40px;
  }

  .md-data-container {
    font-size: 0.81em;
  }

  .md-email-input {
    font-size: 1em;
  }

  .md-email-instruction {
    width: 55%;
  }

  .md-email-sent {
    font-size: 1em;
  }

  .md-privacy-policy-content {
    height: 75vh;
    width: 50vw;
  }
}
</style>
